/* global Word */

import type {
  CharacterStyleRecord,
  CharStyleChangeEntry,
  CharStyleChangeSource,
  CharStyleFormattingFlags,
  CharStyleFormattingOrigin,
  CharStyleState,
} from "src/services";

const NS = "http://manuscript-styling-system/char-style-history/v1";
const EMPTY_XML = `<?xml version="1.0" encoding="UTF-8"?><charStyleHistory xmlns="${NS}"></charStyleHistory>`;

// ────────────────────────────────────────────────────────────────────────────
// Serialization
// ────────────────────────────────────────────────────────────────────────────

function serializeFormattingFlags(
  doc: Document,
  parent: Element,
  tagName: string,
  flags: CharStyleFormattingFlags
): void {
  const el = doc.createElementNS(NS, tagName);
  el.setAttribute("italic", String(flags.italic));
  el.setAttribute("bold", String(flags.bold));
  el.setAttribute("underline", String(flags.underline));
  el.setAttribute("strikethrough", String(flags.strikethrough));
  parent.appendChild(el);
}

function serializeState(
  doc: Document,
  parent: Element,
  tagName: string,
  state: CharStyleState
): void {
  const el = doc.createElementNS(NS, tagName);
  el.setAttribute("characterStyle", state.characterStyle ?? "");
  serializeFormattingFlags(doc, el, "formatting", state.formatting);
  parent.appendChild(el);
}

function serializeRecords(records: CharacterStyleRecord[]): string {
  // console.log("[CharStyleXml][AUDIT] serializeRecords called with", records.length, "records");
  const doc = document.implementation.createDocument(NS, "charStyleHistory", null);
  const root = doc.documentElement;

  for (const record of records) {
    const el = doc.createElementNS(NS, "record");
    el.setAttribute("id", record.id);
    el.setAttribute("paragraphTag", record.paragraphTag);
    el.setAttribute("paragraphUniqueLocalId", record.paragraphUniqueLocalId);
    el.setAttribute("actionType", record.actionType);
    el.setAttribute("actionDetail", record.actionDetail);
    el.setAttribute("actionTarget", record.actionTarget);
    el.setAttribute("spanIndex", record.spanIndex != null ? String(record.spanIndex) : "");
    el.setAttribute("textFingerprint", stripInvalidXmlChars(record.textFingerprint));
    el.setAttribute("rangeStart", String(record.rangeStart));
    el.setAttribute("rangeEnd", String(record.rangeEnd));
    el.setAttribute("isPending", String(record.isPending));
    el.setAttribute("trackedChangeCount", String(record.trackedChangeCount));
    el.setAttribute("ruleId", record.ruleId);
    el.setAttribute("paragraphStyle", record.paragraphStyle);
    el.setAttribute("entityType", record.entityType ?? "");
    el.setAttribute("formattingOrigin", record.formattingOrigin);

    serializeState(doc, el, "originalState", record.originalState);
    serializeState(doc, el, "appliedState", record.appliedState);

    for (const entry of record.history) {
      const entryEl = doc.createElementNS(NS, "entry");
      entryEl.setAttribute("source", entry.source);
      entryEl.setAttribute("timestamp", entry.timestamp);
      serializeState(doc, entryEl, "previousState", entry.previousState);
      serializeState(doc, entryEl, "newState", entry.newState);
      el.appendChild(entryEl);
    }

    root.appendChild(el);
  }

  const xml = new XMLSerializer().serializeToString(doc);

  return xml;
}

function deserializeFormattingFlags(parent: Element, tagName: string): CharStyleFormattingFlags {
  const el = parent.getElementsByTagNameNS(NS, tagName)[0];
  if (!el) {
    return { italic: false, bold: false, underline: false, strikethrough: false };
  }
  return {
    italic: el.getAttribute("italic") === "true",
    bold: el.getAttribute("bold") === "true",
    underline: el.getAttribute("underline") === "true",
    strikethrough: el.getAttribute("strikethrough") === "true",
  };
}

function deserializeState(parent: Element, tagName: string): CharStyleState {
  const el = parent.getElementsByTagNameNS(NS, tagName)[0];
  if (!el) {
    return {
      formatting: { italic: false, bold: false, underline: false, strikethrough: false },
      characterStyle: null,
    };
  }
  const charStyle = el.getAttribute("characterStyle");
  return {
    formatting: deserializeFormattingFlags(el, "formatting"),
    characterStyle: charStyle || null,
  };
}

function deserializeRecords(xml: string): CharacterStyleRecord[] {
  const parsed = new DOMParser().parseFromString(xml, "application/xml");

  const parseError = parsed.querySelector("parsererror");
  if (parseError) {
    console.error("[CharStyleXml] XML parse error:", parseError.textContent);
    return [];
  }

  const records: CharacterStyleRecord[] = [];
  const recordElements = parsed.getElementsByTagNameNS(NS, "record");
  // console.log("[CharStyleXml][AUDIT] Found", recordElements.length, "record elements in XML");

  for (const el of Array.from(recordElements)) {
    const history: CharStyleChangeEntry[] = [];

    for (const entryEl of Array.from(el.getElementsByTagNameNS(NS, "entry"))) {
      // Only get direct child entries (not nested state elements)
      if (entryEl.parentElement !== el) continue;
      history.push({
        previousState: deserializeState(entryEl, "previousState"),
        newState: deserializeState(entryEl, "newState"),
        source: (entryEl.getAttribute("source") ?? "applicator") as CharStyleChangeSource,
        timestamp: entryEl.getAttribute("timestamp") ?? "",
      });
    }

    const spanIndexStr = el.getAttribute("spanIndex") ?? "";
    const entityTypeStr = el.getAttribute("entityType") ?? "";
    const record: CharacterStyleRecord = {
      id: el.getAttribute("id") ?? "",
      paragraphTag: el.getAttribute("paragraphTag") ?? "",
      paragraphUniqueLocalId: el.getAttribute("paragraphUniqueLocalId") ?? "",
      actionType: (el.getAttribute("actionType") ?? "apply_style") as
        | "remove_formatting"
        | "apply_style",
      actionDetail: el.getAttribute("actionDetail") ?? "",
      actionTarget: (el.getAttribute("actionTarget") ?? "span") as "paragraph" | "entity" | "span",
      spanIndex: spanIndexStr ? Number(spanIndexStr) : null,
      textFingerprint: el.getAttribute("textFingerprint") ?? "",
      rangeStart: Number(el.getAttribute("rangeStart") ?? 0),
      rangeEnd: Number(el.getAttribute("rangeEnd") ?? 0),
      originalState: deserializeState(el, "originalState"),
      appliedState: deserializeState(el, "appliedState"),
      isPending: el.getAttribute("isPending") === "true",
      trackedChangeCount: Number(el.getAttribute("trackedChangeCount") ?? 0),
      history,
      ruleId: el.getAttribute("ruleId") ?? "",
      paragraphStyle: el.getAttribute("paragraphStyle") ?? "",
      entityType: entityTypeStr || null,
      formattingOrigin: (el.getAttribute("formattingOrigin") || "source") as CharStyleFormattingOrigin,
    };

    records.push(record);
  }  
  return records;
}

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

export async function initCharStyleHistoryXmlPart(): Promise<void> {
  // console.log("[CharStyleXml] Initializing XML part...");
  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NS);
      parts.load("items");
      await context.sync();

      if (parts.items.length === 0) {
        // console.log("[CharStyleXml] No existing XML part found, creating empty part");
        context.document.customXmlParts.add(EMPTY_XML);
        await context.sync();
        return;
      }

      if (parts.items.length === 1) {
        // console.log("[CharStyleXml] Found 1 existing XML part (OK)");
        return;
      }

      const xmlResults = parts.items.map((part) => part.getXml());
      await context.sync();

      let canonicalIndex = 0;
      let canonicalRecordCount = 0;
      for (let i = 0; i < xmlResults.length; i++) {
        const parsed = new DOMParser().parseFromString(xmlResults[i].value, "application/xml");
        const count = parsed.getElementsByTagNameNS(NS, "record").length;
        if (count > canonicalRecordCount) {
          canonicalIndex = i;
          canonicalRecordCount = count;
        }
      }

      for (let i = 0; i < parts.items.length; i++) {
        if (i !== canonicalIndex) {
          parts.items[i].delete();
        }
      }
      await context.sync();
    });
  } catch (error) {
    console.error(`[CharStyleXml] Init error:`, error);
    throw new Error(
      `Failed to init char style history XML part: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

let writeChain: Promise<void> = Promise.resolve();

export function persistCharStyleRecords(records: CharacterStyleRecord[]): Promise<void> {
  const task = writeChain.then(() => doPersist(records));
  writeChain = task.catch(() => {});
  return task;
}

// eslint-disable-next-line no-control-regex
const INVALID_XML_CHARS_G = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;

function stripInvalidXmlChars(value: string): string {
  return value.replace(INVALID_XML_CHARS_G, "");
}

async function doPersist(records: CharacterStyleRecord[]): Promise<void> {
  const xml = serializeRecords(records);

  try {
    await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NS);
      parts.load("items");
      await context.sync();

      if (parts.items.length === 0) {
        throw new Error(
          `[CharStyleXml] Invariant violation: no XML part found for namespace "${NS}". ` +
            `initCharStyleHistoryXmlPart() must be called before any persist operation.`
        );
      }

      parts.items[0].setXml(xml);
      for (let i = 1; i < parts.items.length; i++) {
        parts.items[i].delete();
        console.warn(`[CharStyleXml] Persist: deleted unexpected duplicate XML part at index ${i}`);
      }

      await context.sync();
    });
  } catch (error) {
    console.error(`[CharStyleXml] Persist error:`, error);
    throw new Error(
      `Failed to persist char style records: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

export async function loadCharStyleRecords(): Promise<CharacterStyleRecord[]> {
  // console.log("[CharStyleXml] Loading records from XML part...");
  try {
    return await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(NS);
      parts.load("items");
      await context.sync();

      if (parts.items.length === 0) {
        // console.log("[CharStyleXml] No XML part found, returning empty");
        return [];
      }
      if (parts.items.length > 1) {
        console.warn(
          `[CharStyleXml] Load: found ${parts.items.length} XML parts — expected exactly 1. Reading items[0].`
        );
      }

      const xmlResult = parts.items[0].getXml();
      await context.sync();

      const records = deserializeRecords(xmlResult.value);
      return records;
    });
  } catch (error) {
    console.error(`[CharStyleXml] Load error:`, error);
    throw new Error(
      `Failed to load char style records: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
